import React from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Phone, Mail, MapPin, Star, CheckCircle } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import FloatingQuoteButton from '../components/FloatingQuoteButton';

// Town data with unique descriptions
const townData = {
  // === HAMPTONS TOWNS ===
  'southampton': {
    name: 'Southampton',
    region: 'hamptons',
    description: 'Welcome to Reflections Cleanings, your trusted local source for exterior cleaning services in Southampton, NY. We take pride in helping maintain the beauty and value of homes in this prestigious Hamptons community. With years of experience, our family-owned, fully insured company knows the unique needs of Southampton homeowners.',
    heroImage: 'https://static.prod-images.emergentagent.com/jobs/034ba03a-ab9b-460a-8d65-ab113159d799/images/488c61b390dd5a04100f27bf6594ad6626cc2609ea05198e46015e8f83a6b882.png',
    mapQuery: 'Southampton,NY',
    route: '/locations/southampton'
  },
  'bridgehampton': {
    name: 'Bridgehampton',
    region: 'hamptons',
    description: 'Welcome to Reflections Cleanings, your trusted local source for exterior cleaning services in Bridgehampton, NY. We understand the refined standards of Bridgehampton properties and deliver exceptional results. Our family-owned, fully insured company specializes in preserving the elegance of Bridgehampton homes.',
    heroImage: 'https://static.prod-images.emergentagent.com/jobs/034ba03a-ab9b-460a-8d65-ab113159d799/images/5a33331c441f537ad27b39e51e36ad19fac7c0ee382ff0fd1c620b0031e3d18b.png',
    mapQuery: 'Bridgehampton,NY',
    route: '/locations/bridgehampton'
  },
  'water-mill': {
    name: 'Water Mill',
    region: 'hamptons',
    description: 'Welcome to Reflections Cleanings, your trusted local source for exterior cleaning services in Water Mill, NY. We appreciate the distinctive character of Water Mill properties and provide meticulous care. Our family-owned, fully insured company is dedicated to maintaining the pristine appearance of Water Mill homes.',
    heroImage: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200',
    mapQuery: 'Water+Mill,NY',
    route: '/locations/water-mill'
  },
  'westhampton': {
    name: 'Westhampton',
    region: 'hamptons',
    description: 'Welcome to Reflections Cleanings, your trusted local source for exterior cleaning services in Westhampton, NY. We serve the Westhampton community with professional, reliable cleaning services. Our family-owned, fully insured company understands the coastal conditions and unique needs of Westhampton properties.',
    heroImage: 'https://static.prod-images.emergentagent.com/jobs/034ba03a-ab9b-460a-8d65-ab113159d799/images/6805417f3da0a16a1cf58a190a2c7c923faabee5a35f0a5fced3373ef5e37d7d.png',
    mapQuery: 'Westhampton,NY',
    route: '/locations/westhampton'
  },
  'sag-harbor': {
    name: 'Sag Harbor',
    region: 'hamptons',
    description: 'Welcome to Reflections Cleanings, your trusted local source for exterior cleaning services in Sag Harbor, NY. We cherish the historic charm and waterfront beauty of Sag Harbor properties. Our family-owned, fully insured company provides careful, expert cleaning that respects the character of Sag Harbor homes.',
    heroImage: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1200',
    mapQuery: 'Sag+Harbor,NY',
    route: '/locations/sag-harbor'
  },
  'east-hampton': {
    name: 'East Hampton',
    region: 'hamptons',
    description: 'Welcome to Reflections Cleanings, your trusted local source for exterior cleaning services in East Hampton, NY. We proudly serve the iconic East Hampton community with premium cleaning services. Our family-owned, fully insured company meets the high standards expected in East Hampton.',
    heroImage: 'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=1200',
    mapQuery: 'East+Hampton,NY',
    route: '/locations/east-hampton'
  },
  'remsenburg': {
    name: 'Remsenburg',
    region: 'hamptons',
    description: 'Welcome to Reflections Cleanings, your trusted local source for exterior cleaning services in Remsenburg, NY. We serve the quiet, exclusive Remsenburg community with discreet, professional service. Our family-owned, fully insured company provides the personalized attention Remsenburg homeowners deserve.',
    heroImage: 'https://static.prod-images.emergentagent.com/jobs/034ba03a-ab9b-460a-8d65-ab113159d799/images/4c7446b01a6e9021b8aaf1139015cca7ca288a3995d97ab289262138331b4232.png',
    mapQuery: 'Remsenburg,NY',
    route: '/locations/remsenburg'
  },
  // === NASSAU COUNTY TOWNS ===
  'old-westbury-ny': {
    name: 'Old Westbury',
    region: 'nassau',
    description: 'Serving historic estates and luxury properties throughout Old Westbury, Reflections Cleanings provides high-end house washing, roof cleaning, window cleaning, and gutter maintenance tailored to Gold Coast homes. Large estates, gated properties, and custom architecture require a professional approach — and we deliver results that truly reflect the quality of your home.',
    heroImage: 'https://static.prod-images.emergentagent.com/jobs/034ba03a-ab9b-460a-8d65-ab113159d799/images/589c9e40e8d4876feba1f703b90974286553d30725ae2ce8ed031205d1e668de.png',
    mapQuery: 'Old+Westbury,NY',
    route: '/old-westbury-ny'
  },
  'brookville-ny': {
    name: 'Brookville',
    region: 'nassau',
    description: 'Brookville is known for expansive private estates and heavily wooded properties. Moisture and tree coverage often lead to algae and organic buildup on siding and roofing systems. Reflections Cleanings specializes in safe soft washing solutions that preserve natural materials while restoring their original appearance.',
    heroImage: 'https://static.prod-images.emergentagent.com/jobs/034ba03a-ab9b-460a-8d65-ab113159d799/images/549d9399300963c06d77adc2ecb8b570122b17c03324cc6c136f9bb6f242eed8.png',
    mapQuery: 'Brookville,NY',
    route: '/brookville-ny'
  },
  'muttontown-ny': {
    name: 'Muttontown',
    region: 'nassau',
    description: 'Luxury homes in Muttontown are surrounded by mature landscaping and natural elements that require consistent exterior maintenance. We provide professional roof washing, cedar cleaning, and estate window cleaning to ensure your property reflects its full beauty year-round.',
    heroImage: 'https://static.prod-images.emergentagent.com/jobs/034ba03a-ab9b-460a-8d65-ab113159d799/images/ccf6f870ead5ae3a251381b54fd2d792944af00b5d6e6cc7e495b00aba3c95f9.png',
    mapQuery: 'Muttontown,NY',
    route: '/muttontown-ny'
  },
  'sands-point-ny': {
    name: 'Sands Point',
    region: 'nassau',
    description: 'Waterfront homes in Sands Point face salt exposure, moisture, and environmental buildup. Reflections Cleanings offers specialized exterior cleaning services designed for coastal conditions, restoring clarity to windows and protecting siding from salt-related staining.',
    heroImage: 'https://static.prod-images.emergentagent.com/jobs/034ba03a-ab9b-460a-8d65-ab113159d799/images/eae0bc99b0781620b76095af8a1faf8f2cbd3d348de053721e68361a2afd4521.png',
    mapQuery: 'Sands+Point,NY',
    route: '/sands-point-ny'
  },
  'oyster-bay-cove-ny': {
    name: 'Oyster Bay Cove',
    region: 'nassau',
    description: 'Oyster Bay Cove features historic and architecturally significant homes that demand careful, knowledgeable cleaning methods. Our soft washing systems remove algae and discoloration without damaging delicate materials, preserving the elegance of North Shore properties.',
    heroImage: 'https://static.prod-images.emergentagent.com/jobs/034ba03a-ab9b-460a-8d65-ab113159d799/images/61c0a5a36762c89e921e5fd4ade8e2cc3d47d218494c5412bb82735cf37ab8b9.png',
    mapQuery: 'Oyster+Bay+Cove,NY',
    route: '/oyster-bay-cove-ny'
  },
  'plandome-ny': {
    name: 'Plandome',
    region: 'nassau',
    description: 'Plandome homeowners take pride in beautifully maintained properties with manicured landscaping and detailed exteriors. Reflections Cleanings provides streak-free window cleaning, roof treatments, and exterior washing services that elevate curb appeal and protect long-term value.',
    heroImage: 'https://static.prod-images.emergentagent.com/jobs/034ba03a-ab9b-460a-8d65-ab113159d799/images/6b95e05f858f89386375db6a0225eecbffc037681901064f6d69a4df347f1593.png',
    mapQuery: 'Plandome,NY',
    route: '/plandome-ny'
  },
  'hewlett-bay-park-ny': {
    name: 'Hewlett Bay Park',
    region: 'nassau',
    description: 'Hewlett Bay Park offers luxury waterfront and South Shore homes that require consistent exterior upkeep. From roof cleaning to soft washing and precision window care, Reflections Cleanings ensures your home reflects sophistication and care.',
    heroImage: 'https://static.prod-images.emergentagent.com/jobs/034ba03a-ab9b-460a-8d65-ab113159d799/images/0638a5d893021f6b52577320a5172d26f2cf0b5e8835f655464b27036104cb44.png',
    mapQuery: 'Hewlett+Bay+Park,NY',
    route: '/hewlett-bay-park-ny'
  }
};

const services = [
  {
    name: 'House Washing',
    description: 'Safe and effective soft washing to remove dirt, algae, and mold from your home\'s siding, eaves, and exterior surfaces.',
    image: 'https://customer-assets.emergentagent.com/job_clean-hamptons-demo/artifacts/90x60b57_Powerwashing%20Photo.png',
    link: '/services/house-washing'
  },
  {
    name: 'Roof Cleaning',
    description: 'Gentle soft washing to clean and protect your roof from black streaks, moss, and lichen, extending its life.',
    image: 'https://images.unsplash.com/photo-1632759145351-1d592919f522?w=600',
    link: '/services/house-washing'
  },
  {
    name: 'Window Cleaning',
    description: 'Spotless, streak-free window cleaning for a crystal clear view and a brighter home.',
    image: 'https://customer-assets.emergentagent.com/job_clean-hamptons-demo/artifacts/hjhsuv06_TISK5480.JPG',
    link: '/services/window-cleaning'
  },
  {
    name: 'Gutter Cleaning',
    description: 'Thorough gutter cleaning to prevent clogs, overflows and water damage to your home.',
    image: '/images/services/gutter-cleaning.jpg',
    link: '/services/gutter-cleaning'
  }
];

const TownPage = ({ townSlug }) => {
  const { town: paramTown } = useParams();
  const navigate = useNavigate();
  const slug = townSlug || paramTown;
  const data = townData[slug];

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Location not found</h1>
          <Link to="/" className="text-blue-600 hover:underline">Return to homepage</Link>
        </div>
      </div>
    );
  }

  const isNassau = data.region === 'nassau';
  const regionLabel = isNassau ? 'Nassau County' : 'Hamptons';
  const regionHubLink = isNassau ? '/nassau-county-ny' : null;

  const goToQuote = () => {
    window.location.href = '/#quote';
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-24">
        <div className="w-full h-[400px] relative">
          <img
            src={data.heroImage}
            alt={`Exterior cleaning services in ${data.name}`}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 to-transparent" />
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Content */}
            <div className="lg:col-span-2">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                Top-Rated Exterior Cleaning in {data.name}
              </h1>
              <p className="text-gray-700 leading-relaxed mb-8">
                {data.description}
              </p>

              {/* Map */}
              <div className="rounded-lg overflow-hidden shadow-md mb-8">
                <iframe
                  title={`Map of ${data.name}`}
                  src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${data.mapQuery}&zoom=12`}
                  width="100%"
                  height="300"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>

            {/* Right Sidebar - Contact Card */}
            <div className="lg:col-span-1">
              <div className="bg-gray-900 text-white rounded-lg p-6 sticky top-28">
                <div className="space-y-4 mb-6">
                  <a href="tel:631-660-8164" className="flex items-center gap-3 hover:text-blue-400 transition-colors">
                    <Phone className="w-5 h-5" />
                    <span className="font-semibold">(631) 660-8164</span>
                  </a>
                  <a href="mailto:info@refcleanings.com" className="flex items-center gap-3 hover:text-blue-400 transition-colors">
                    <Mail className="w-5 h-5" />
                    <span>info@refcleanings.com</span>
                  </a>
                </div>

                <button
                  onClick={goToQuote}
                  className="w-full bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-bold py-3 px-6 rounded-lg transition-colors mb-6"
                >
                  Get a Free Quote
                </button>

                {/* Google Reviews */}
                <div className="flex items-center gap-2 pt-4 border-t border-gray-700">
                  <span className="text-xl font-bold text-yellow-500">Google</span>
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-yellow-500 text-yellow-500" />
                    ))}
                  </div>
                  <span className="text-sm text-gray-400">5.0 on Google</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">Based on 100+ Google Reviews</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-8">
            Our {data.name} Exterior Cleaning Services
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <Link
                key={index}
                to={service.link}
                className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow group"
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-4">
                  <h3 className="font-bold text-gray-900 mb-2">{service.name}</h3>
                  <p className="text-sm text-gray-600 line-clamp-3">{service.description}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-8">
            Why {data.name} Homeowners Choose Us
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(isNassau ? [
              { title: 'Estate-Level Expertise', desc: `We specialize in luxury and estate-level properties throughout ${data.name} and Nassau County.` },
              { title: 'Fully Insured', desc: 'Complete liability and workers\' compensation coverage for your peace of mind.' },
              { title: 'Soft Washing Specialists', desc: 'Our gentle soft washing systems protect delicate materials like cedar, slate, and custom stonework.' },
              { title: 'Discreet & Professional', desc: 'Respectful service tailored to the privacy and standards of premium properties.' },
              { title: 'Trusted Across Long Island', desc: 'Serving the Hamptons and Nassau County with a proven track record of excellence.' },
              { title: 'Fair Pricing', desc: 'Competitive rates with no hidden fees. Free quotes within 24 hours.' }
            ] : [
              { title: 'Local Expertise', desc: `We know ${data.name} properties and the unique challenges of coastal Hamptons living.` },
              { title: 'Fully Insured', desc: 'Complete liability and workers\' compensation coverage for your peace of mind.' },
              { title: '100% Satisfaction', desc: 'We stand behind our work with a satisfaction guarantee on every job.' },
              { title: 'Eco-Friendly', desc: 'Safe, biodegradable cleaning solutions that protect your landscaping.' },
              { title: 'Prompt Service', desc: 'Quick response times and flexible scheduling to fit your needs.' },
              { title: 'Fair Pricing', desc: 'Competitive rates with no hidden fees. Free quotes within 24 hours.' }
            ]).map((item, index) => (
              <div key={index} className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-gray-900">{item.title}</h3>
                  <p className="text-sm text-gray-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-4">
            Get a Free {data.name} Cleaning Quote
          </h2>
          <p className="text-blue-100 mb-8">
            Contact <span className="text-gradient-reflections font-bold">Reflections Cleanings</span> and let us make your {data.name} home shine.
          </p>
          <button
            onClick={goToQuote}
            className="bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-bold py-4 px-8 rounded-lg transition-colors text-lg"
          >
            Request a Free Quote
          </button>
        </div>
      </section>

      {/* Other Locations */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-xl font-bold text-gray-900 mb-6">
            We Also Serve These {regionLabel} Communities
          </h2>
          <div className="flex flex-wrap gap-3">
            {Object.entries(townData)
              .filter(([s, t]) => s !== slug && t.region === data.region)
              .map(([s, townInfo]) => (
                <Link
                  key={s}
                  to={townInfo.route}
                  className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm hover:shadow-md transition-shadow text-gray-700 hover:text-blue-600"
                >
                  <MapPin className="w-4 h-4" />
                  {townInfo.name}
                </Link>
              ))}
          </div>
          {isNassau && (
            <div className="mt-6">
              <Link
                to="/nassau-county-ny"
                className="text-blue-600 hover:text-blue-700 font-semibold"
              >
                View all Nassau County service areas &rarr;
              </Link>
            </div>
          )}
        </div>
      </section>

      <Footer />
      <FloatingQuoteButton />
    </div>
  );
};

export default TownPage;
